/**
 * Generation UI state + the wiring of the engine onto the real builder (feature 0059 lot 1d).
 *
 * The ENGINE is pure and tested with fakes (engine.ts); this store is the thin impure rim: it arms
 * itself from the `?generate=<categoryId>` boot param, gathers the two facts the engine needs from the
 * outside (store identity, current row locale), adapts five builderStore/aiApi calls into EngineDeps,
 * and folds the engine's events into what the GenerationPanel renders.
 *
 * A run is ONE-SHOT per arm: refresh mid-run simply stops it (the theme copy stands wherever the run
 * had not reached — the §5.3 safety net makes an interrupted run a valid store, not a broken one).
 */
import { sectionRegistry } from '../../canvas/sectionRegistry';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  collectImageSlots,
  collectTextSlots,
  getThemeListing,
  planPage,
  type TemplateKey,
} from '@aquilixshop/ui-react';
import { GENERATING_MEDIA_URI, HERO_IMAGE_TYPES, type ImageTarget } from './engine';
import { AiUnavailableError, aiApi } from '../../lib/aiApi';
import { catalogApi, productResponseToStorefront } from '../../lib/catalogApi';
import { storeApi } from '../../lib/storeApi';
import { deriveStorefrontUrl } from '../../boot/config';
import { serializeComposed, themeWorkspaceHandle, useBuilderStore } from '../builderStore';
import { runGeneration, themeIdFor, type GenerationEvent, type GenerationSummary } from './engine';
import { planCatalogGrounding } from './catalogGrounding';
import { COMPOSER_MENU, sanitizeComposition } from './composition';
import { toSlotDto, valuesToPatches } from './slotKeys';
import { sectionDisplayName } from '../../shell/sectionVariantLabel';
import {
  buildImageSubject,
  buildRetouchBrief,
  scopeWording,
  slotInScope,
  type RetouchOptions,
  type RetouchProduct,
  type RetouchScope,
} from './retouche';
import { blockLabel, fieldLabel } from '../../canvas/CanvasFrame';
import { bt } from '../../i18n';

/**
 * CE QUE LA VENDEUSE A DÉSIGNÉ, EN TOUTES LETTRES — pour la phrase que le modèle lit («tu retouches
 * le bloc « Titre » de la section « Hero »»). Les noms viennent des mêmes tables que le cadre du
 * canvas et que le panneau : ce que le brief nomme est ce qu'elle voit encadré à l'écran.
 */
function scopeTargetName(
  state: ReturnType<typeof useBuilderStore.getState>,
  scope: RetouchScope,
): { wording: string } {
  const section = state.sectionsById[scope.sectionId];
  const sectionName = section ? sectionDisplayName(section) : 'la section';
  const target = scope.fieldId
    ? fieldLabel(section, scope.fieldId)
    : scope.blockId
      ? blockLabel(section, scope.blockId)
      : undefined;
  return { wording: scopeWording(scope, { section: sectionName, target }) };
}

/**
 * COMBIEN DE CHAMPS PAR APPEL. Huit, et le chiffre vient d'une mesure, pas d'un pouce levé : le
 * 27/08, l'appel qui a réussi a écrit 25 champs en ~49 s, soit ~2 s par champ. Un paquet de huit
 * revient donc autour de 15-20 s, loin sous le délai d'un appel (60 s), et la vendeuse voit
 * l'avancement bouger plusieurs fois au lieu d'un sablier unique.
 */
const TEXT_BATCH = 8;

/** The image value currently standing at a regen target — restored verbatim when the regen fails. */
function readImageValue(
  state: ReturnType<typeof useBuilderStore.getState>,
  target: { sectionId: string; blockId?: string; fieldId: string },
): import('@aquilixshop/ui-react').SettingValue | null {
  const section = state.sectionsById[target.sectionId];
  if (!section) return null;
  type V = import('@aquilixshop/ui-react').SettingValue;
  if (!target.blockId) return (section.settings?.[target.fieldId] as V | undefined) ?? null;
  type B = { id: string; settings?: Record<string, V>; blocks?: readonly B[] };
  const findBlock = (blocks: readonly B[] | undefined): V | null | undefined => {
    for (const b of blocks ?? []) {
      if (b.id === target.blockId) return b.settings?.[target.fieldId] ?? null;
      const deep = findBlock(b.blocks);
      if (deep !== undefined) return deep;
    }
    return undefined;
  };
  return findBlock(section.blocks as unknown as readonly B[]) ?? null;
}

/** Completion-guard key: the per-click run id when the dashboard minted one, else category+brief. */
function doneKey(runId: string | null, categoryId: string, brief: string): string {
  return runId ? `aqx-generation-done:run:${runId}` : `aqx-generation-done:${categoryId}::${brief}`;
}

export interface SectionRow {
  sectionId: string;
  /** running | done | failed | integrity | keep */
  state: 'running' | 'done' | 'failed' | 'integrity' | 'keep';
  activity?: string;
}

export interface GenerationState {
  /** idle → armed (boot param seen, intro form shown) → running → done | unavailable | failed */
  status: 'idle' | 'armed' | 'running' | 'done' | 'unavailable';
  /**
   * `panel` = the in-editor card (manual URL); `takeover` = the full-screen creation modal (the
   * dashboard AI-card flow, founder 2026-08-20: the seller WATCHES the store being built, chrome
   * hidden, then chooses «Explorer» or «Ouvrir le builder»).
   */
  mode: 'panel' | 'takeover';
  categoryId: string | null;
  /** The phrase typed on the dashboard's AI card — pre-fills the intro form so it is typed ONCE. */
  initialBrief: string;
  /** The product the store is built AROUND (founder 2026-08-20) — grounds copy, images and preview. */
  productId: string | null;
  /** Per-click run id from the dashboard URL — keys the completion guard (reload ≠ new purchase). */
  runId: string | null;
  /** Marketing angle shots generated AND saved into the chosen product's media this run. */
  productPhotos: number;
  /** «Ask for changes» (lot 2): the section being regenerated right now, null when none. */
  regenSectionId: string | null;
  /** Terse reason when the LAST targeted regeneration failed — cleared on the next attempt. */
  regenError: string | null;
  /**
   * L'AVANCEMENT D'UNE RETOUCHE, en paquets de champs — `null` quand rien ne tourne.
   *
   * Une fiche produit porte vingt-cinq champs : le 27/08, tout partait en UN appel, et la vendeuse
   * regardait un sablier muet pendant deux minutes avant un délai dépassé. On envoie par paquets, et
   * on dit où on en est.
   */
  regenProgress: { done: number; total: number } | null;
  /** Ce que la DERNIÈRE retouche a vraiment changé — pour le dire, au lieu de refermer sur du vide. */
  regenResult: { texts: number; images: number; failedBatches: number } | null;
  /**
   * True once «Explorer ma boutique» ran INSIDE the dashboard's creation modal: the iframe then
   * shows the component PREVIEW only — never the editor chrome (founder 2026-08-21: «la modal est
   * un preview des composants, pas d'ouverture de builder dedans»). Editing goes through «Ouvrir le
   * builder», which top-navigates out of the modal.
   */
  embeddedPreview: boolean;
  /** «Mettre ma boutique en ligne» — the end card's primary door (founder 2026-08-21: the generated
   *  store lived only as workspace drafts; the seller's live storefront kept serving the OLD store). */
  publishState: 'idle' | 'publishing' | 'published' | 'failed';
  /** The live storefront URL once published — the «Voir ma boutique» link. */
  storefrontUrl: string | null;
  /** What the étiquette says right now ("Écrit les questions fréquentes…"). */
  activity: string | null;
  currentPage: string | null;
  sections: SectionRow[];
  /** Per-page build state — feeds the takeover's side rail (En attente / En cours / Terminé). */
  pageStates: Record<string, 'pending' | 'running' | 'done' | 'absent'>;
  /**
   * Section ids REVEALED so far, in build order — the «à partir de zéro» contract (founder
   * 2026-08-20): during a takeover run the canvas renders ONLY these, so the page assembles brick by
   * brick instead of a finished template appearing. Presentation-only; the document is never touched.
   */
  builtSectionIds: string[];
  summary: GenerationSummary | null;
  /** Section types the integrity rule left to the seller — the "à toi de remplir" list. */
  integritySkips: string[];

  arm(
    categoryId: string,
    initialBrief?: string,
    autostart?: boolean,
    productId?: string | null,
    runId?: string | null,
  ): void;
  start(storeDescription: string): Promise<void>;
  /** Takeover end-state: enter storefront-like preview so the seller can walk her store. */
  /** Copy the generated workspace over the live store and publish it — see publishState. */
  publishStore(): Promise<void>;
  /**
   * «Demander un changement» (lot 2, élargi le 26/08): regenerate what the seller SELECTED — the
   * whole section, one block, or one field ({@link RetouchOptions.scope}) — from her instruction.
   * Texts run in RETOUCHE mode (subject kept, instruction applied) and/or images (streamed, product
   * cover as reference), grounded in the store's REAL product + page context. She may name the AI
   * that serves this one prompt. One undo frame per surface; a failure restores what stood.
   */
  regenerateSection(
    sectionId: string,
    instruction: string,
    surfaces: { texts: boolean; image: boolean },
    options?: RetouchOptions,
  ): Promise<void>;
  explore(): void;
  /** Takeover end-state: dismiss into the normal editor. */
  openEditor(): void;
  dismiss(): void;
}

export const useGenerationStore = create<GenerationState>()(
  immer((set, get) => ({
    status: 'idle',
    mode: 'panel',
    categoryId: null,
    initialBrief: '',
    productId: null,
    activity: null,
    currentPage: null,
    sections: [],
    builtSectionIds: [],
    pageStates: {},
    summary: null,
    integritySkips: [],
    runId: null,
    productPhotos: 0,
    regenSectionId: null,
    regenError: null,
    regenProgress: null,
    regenResult: null,
    embeddedPreview: false,
    publishState: 'idle',
    storefrontUrl: null,

    arm(categoryId, initialBrief = '', autostart = false, productId = null, runId = null) {
      // IDEMPOTENT FROM IDLE ONLY (the loop fix, 2026-08-20): React StrictMode double-mounts the
      // router in dev, its useRef guard is reborn empty, and arm() used to fire twice — TWO
      // interleaved runs, the second resetting the scene after the first completed («ça termine puis
      // repart de 0»). A store-level guard survives any remount; dismiss() is the only way back.
      if (get().status !== 'idle') return;
      // Arm only for a category whose theme is real — a stale URL must not open a dead panel.
      const themeId = themeIdFor(categoryId);
      if (!getThemeListing(themeId)) return;
      const brief = initialBrief.slice(0, 600);
      set((s) => {
        s.status = 'armed';
        s.mode = autostart && brief ? 'takeover' : 'panel';
        s.categoryId = categoryId;
        s.initialBrief = brief;
        s.productId = productId;
        s.runId = runId;
        s.embeddedPreview = false;
      });
      // The dashboard flow already asked the ONE question — the takeover starts itself. The manual
      // URL keeps the confirm click: a bookmark must never fire a paid run on open.
      //
      // COMPLETION GUARD (belt-and-braces under the stable-src fix): if THIS category+brief already
      // completed in this browser session, an iframe/page reload must not re-fire a paid run — it
      // boots into the normal builder on the generated workspace instead.
      // Keyed on the dashboard's per-click `run` id when present: a RELOAD replays the same URL
      // (same id → guard holds, no double-billed run), while a fresh «Générer» click mints a new id —
      // re-running with the SAME prompt is a legitimate, deliberate purchase, not an accident.
      if (autostart && brief) {
        if (sessionStorage.getItem(doneKey(runId, categoryId, brief))) {
          set((s) => {
            s.status = 'idle';
            s.mode = 'panel';
          });
          return;
        }
        void get().start(brief);
      }
    },

    async start(storeDescription) {
      const categoryId = get().categoryId;
      if (!categoryId || get().status !== 'armed') return;
      set((s) => {
        s.status = 'running';
        s.activity = bt('chrome.gen.act.preparing', 'Preparing the store…');
        s.pageStates = { product: 'pending', index: 'pending' };
      });

      const builder = () => useBuilderStore.getState();
      const wsHandle = themeWorkspaceHandle(themeIdFor(categoryId));
      // The takeover autostarts DURING the applyTheme boot: the workspace drafts may still be seeding.
      // Wait for the boot to land on the workspace (or time out and let openPage fend for itself).
      const deadline = Date.now() + 45_000;
      while (
        Date.now() < deadline &&
        !(builder().route.handle === wsHandle && builder().loadState === 'ready')
      ) {
        await new Promise((r) => setTimeout(r, 500));
      }
      // The row locale of the workspace the applyTheme boot opened — every page of the run uses it.
      const rowLocale = builder().route.locale;
      // The language the AI WRITES in (0059 §9: directly in the store's language, never translated) —
      // and it is the ROW's language, never a fallback: the 2026-08-20 storeTest run had language=bg,
      // the old ['fr','ar','en'] guard silently wrote FRENCH copy into the Bulgarian rows, and the
      // founder (viewing another row) saw an untouched theme. Row and text move together, always.
      const textLocale = rowLocale || builder().storeLanguage || 'fr';

      let storeName = '';
      try {
        storeName = (await storeApi.getIdentity()).storeName;
      } catch {
        storeName = 'La boutique'; // identity fetch down ≠ generation down — degrade, don't stop
      }

      // THE CHOSEN PRODUCT (founder 2026-08-20: the flow starts from a product + a detailed
      // prompt): its name/description ground every section, its cover replaces the theme's demo hero
      // when AI images can't, and the product-page PREVIEW binds to it — the seller watches HIS
      // product's page being written. Everything degrades without blocking (a new store is empty).
      let productContext: string | undefined;
      let heroImageFallback: { url: string; alt: string } | undefined;
      const chosenId = get().productId;
      if (chosenId) {
        try {
          const prod = await catalogApi.getProduct(chosenId);
          builder().setPreviewProduct(productResponseToStorefront(prod));
          const desc = prod.description
            .replace(/<[^>]+>/g, ' ')
            .replace(/[ \t\n]+/g, ' ')
            .trim()
            .slice(0, 400);
          productContext =
            'Produit principal — la boutique est construite AUTOUR de lui : ' +
            prod.name +
            '.' +
            (desc ? ' Description réelle : ' + desc : '');
          // The first PUBLIC PHOTO — never a dashboard-only shot, never a clip (the hero paints an `<img>`).
          const cover = prod.images.find((i) => !i.internalOnly && i.kind !== 'video');
          if (cover) heroImageFallback = { url: cover.url, alt: prod.name };
        } catch {
          // fall through to the catalog names below
        }
      }
      try {
        await builder().loadCatalog();
        const names = builder()
          .catalog.filter((prod) => prod.status !== 'ARCHIVED' && prod.id !== chosenId)
          .slice(0, 4)
          .map((prod) => prod.name)
          .filter(Boolean);
        if (names.length > 0) {
          const others = 'Autres produits vendus : ' + names.join(' · ');
          productContext = productContext ? productContext + ' ' + others : others;
        }
      } catch {
        // no catalog — the brief alone carries the store
      }

      // PHOTOS PRODUIT (founder 2026-08-21: «générer aussi les images produits selon différents
      // angles marketing et les enregistrer dans les médias produits») — three marketing angles,
      // generated in PARALLEL with the pages (independent API budget) and SAVED server-side into the
      // product's media. When they land, the preview product is re-fetched so the canvas gallery
      // fills under the seller's eyes. Every failure degrades silently: an angle that dies never blocks the run.
      const productPhotosTask: Promise<void> = (async () => {
        if (!chosenId || !productContext) return;
        const subject = productContext.slice(0, 260);
        // Le back envoie la COVER du produit au modèle (image-à-image) ; la consigne verrouille :
        // le glitch du 21/08 était un produit INVENTÉ par le text-to-image.
        const keep =
          'Reproduis EXACTEMENT le produit de la photo fournie — même forme, mêmes couleurs, ' +
          'mêmes matériaux, même marque. Le produit ne change pas ; seule la mise en scène change. ';
        const angles: { prompt: string; aspectRatio: '1:1' }[] = [
          {
            prompt:
              `${keep}Photographie packshot e-commerce studio : ${subject} Produit seul, fond uni ` +
              'neutre, éclairage doux, ombres délicates. Sans texte ajouté, sans lettrage.',
            aspectRatio: '1:1',
          },
          {
            prompt:
              `${keep}Photographie lifestyle : ${subject} Le produit en situation réelle ` +
              "d'utilisation, ambiance naturelle, personne floue en arrière-plan. Sans texte ajouté, sans lettrage.",
            aspectRatio: '1:1',
          },
          {
            prompt:
              `${keep}Photographie macro : ${subject} Gros plan sur les détails, matériaux et ` +
              'finitions du produit, faible profondeur de champ. Sans texte ajouté, sans lettrage.',
            aspectRatio: '1:1',
          },
        ];
        await Promise.all(
          angles.map(async (angle) => {
            try {
              await aiApi.generateProductImage({ ...angle, productId: chosenId });
              set((s) => {
                s.productPhotos += 1;
              });
            } catch {
              // 503 (pas de clé) ou échec ponctuel — les autres angles continuent
            }
          }),
        );
        if (get().productPhotos > 0) {
          try {
            const fresh = await catalogApi.getProduct(chosenId);
            builder().setPreviewProduct(productResponseToStorefront(fresh));
          } catch {
            // la galerie se rafraîchira à la prochaine ouverture
          }
        }
      })();

      // THE MARKETING COMPOSER (founder 2026-08-21): one call, the product + the full component
      // menu in, each page's ordered section list out. Fails → the fixed blueprint, silently — the
      // composer may only ever improve the pages, never block or degrade the run.
      let composition: { product: string[] | null; index: string[] | null } | undefined;
      set((s) => {
        s.activity = bt(
          'chrome.gen.act.composing',
          'Composing your store — choosing the sections, product in hand…',
        );
      });
      try {
        const answer = await aiApi.compose({
          description: storeDescription,
          productContext: productContext ?? '',
          sections: [...COMPOSER_MENU],
        });
        if (!answer.degraded) {
          composition = {
            product: sanitizeComposition(answer.product, 'product'),
            index: sanitizeComposition(answer.index, 'index'),
          };
        }
      } catch {
        // transport/503 — the blueprint takes over
      }

      const summary = await runGeneration(
        {
          categoryId,
          storeName,
          storeDescription,
          locale: textLocale,
          productContext,
          heroImageFallback,
          composition,
        },
        {
          async openPage(template: TemplateKey) {
            try {
              await builder().load({ template, handle: wsHandle, locale: rowLocale });
              return builder().loadState === 'ready';
            } catch {
              return false; // a theme without this template — the run continues without it
            }
          },
          composePage(types) {
            // FROM SCRATCH (founder 2026-08-21): drop every template-zone section the theme seeded,
            // then insert the blueprint from the component library — schema presets, store language,
            // zero theme content. Header/footer chrome stays (it is the store's frame, not a
            // component choice). Best-effort: a compose hiccup leaves the theme page standing.
            try {
              const state = builder();
              const doc = serializeComposed(state);
              for (const section of doc.sections) {
                if (section.type === 'header' || section.type === 'footer') continue;
                state.removeSection(section.id);
              }
              // Insert BEFORE the footer's slot in the order array — chrome pinning tolerates
              // stragglers at render time, but the document should read in true order.
              const orderNow = builder().order;
              const footerAt = orderNow.findIndex(
                (id) => builder().sectionsById[id]?.type === 'footer',
              );
              let at = footerAt === -1 ? orderNow.length : footerAt;
              for (const type of types) {
                builder().insertSection(type, at);
                at += 1;
              }
              builder().selectSection(null);
              // CATALOG GROUNDING on the fresh sections: real products in every picker, or the
              // section is disabled — never the demo shelf (the fake «BERQ» grid). TWO passes: a
              // from-scratch grid has ZERO cards, so pass 1 CREATES them (addBlocks) and pass 2
              // re-plans on the grown document and binds each card to a real product.
              const active = builder()
                .catalog.filter((p) => p.status?.toUpperCase() === 'ACTIVE')
                .map((p) => p.id);
              const replan = () =>
                planCatalogGrounding(
                  serializeComposed(builder()),
                  sectionRegistry,
                  active,
                  get().productId,
                );
              let plan = replan();
              if (plan.addBlocks.length > 0) {
                for (const a of plan.addBlocks) {
                  for (let i = 0; i < a.count; i += 1) builder().addBlock(a.sectionId, a.blockType);
                }
                builder().selectSection(null); // addBlock selects the new block — the run selects nothing
                plan = replan();
              }
              for (const b of plan.bindBlock) {
                builder().updateBlockSettings(b.sectionId, b.blockId, {
                  [b.fieldId]: { kind: 'product', id: b.productId },
                });
              }
              for (const f of plan.bindSection) {
                builder().updateSectionSettings(f.sectionId, {
                  [f.fieldId]: { kind: 'product', id: f.productId },
                });
              }
              for (const id of plan.disable) builder().toggleSectionDisabled(id);
            } catch {
              // composing is the run's spine but must not throw — the engine plans whatever stands
            }
          },
          planCurrentPage(recipe) {
            return planPage(serializeComposed(builder()), recipe, sectionRegistry);
          },
          generate(request) {
            return aiApi.generateSection(request).then((r) => r.values);
          },
          applyPatches(patches) {
            return builder().applyGeneratedText(patches);
          },
          reveal(sectionId) {
            // Panel mode: the selection frame + auto-scroll are the liseré (§5.2). Takeover mode: the
            // GHOST owns attention and auto-scroll — a selection frame on a still-hidden section only
            // flickers, so the takeover skips it.
            if (get().mode === 'takeover') return;
            builder().selectSection(sectionId);
            builder().requestCanvasReveal();
          },
          listImageTargets() {
            // The whole schema-derived image surface («100 % IA»), hero first so the page's opener is
            // the first pixel the seller sees change — the rest follows in render order.
            const slots = collectImageSlots(serializeComposed(builder()), sectionRegistry);
            const targets: ImageTarget[] = slots.flatMap((slot) => {
              const sectionId = slot.path[0];
              if (!sectionId) return [];
              return [
                {
                  sectionId,
                  // updateBlockSettings resolves a block by id at ANY depth — the leaf id is enough.
                  blockId: slot.path.length > 1 ? slot.path[slot.path.length - 1] : undefined,
                  fieldId: slot.fieldId,
                  sectionType: slot.sectionType,
                  context: slot.context,
                  hadImage: slot.hasCurrentImage,
                  generatable: slot.generatable,
                },
              ];
            });
            const heroAt = targets.findIndex(
              (t) => !t.blockId && HERO_IMAGE_TYPES.includes(t.sectionType),
            );
            if (heroAt > 0) targets.unshift(...targets.splice(heroAt, 1));
            return targets;
          },
          async generateImage(request, onPartial) {
            // STREAMING first (partial frames materialize in the canvas), with the chosen product's
            // cover as the server-side reference — a hero that shows the product shows THE product.
            // Any streaming failure that is not a provider verdict falls back to the plain call.
            const chosen = get().productId ?? undefined;
            if (onPartial) {
              try {
                return await aiApi.generateImageStream(
                  { ...request, productId: chosen },
                  onPartial,
                );
              } catch (e) {
                if (e instanceof AiUnavailableError) throw e;
                // fall through — one plain retry below
              }
            }
            return aiApi.generateImage(request);
          },
          clearImage(target) {
            // null (not '') — the renderers show PlaceholderMedia on an ABSENT image; an ImageValue
            // with an empty url would be a broken <img>, not the neutral scenery.
            if (target.blockId) {
              builder().updateBlockSettings(target.sectionId, target.blockId, {
                [target.fieldId]: null,
              });
            } else {
              builder().updateSectionSettings(target.sectionId, { [target.fieldId]: null });
            }
          },
          applyImage(target, url, alt) {
            // Through the SAME public edit paths as the inspector: history frame + autosave included.
            if (target.blockId) {
              builder().updateBlockSettings(target.sectionId, target.blockId, {
                [target.fieldId]: { url, alt },
              });
            } else {
              builder().updateSectionSettings(target.sectionId, { [target.fieldId]: { url, alt } });
            }
          },
        },
        (event: GenerationEvent) => {
          set((s) => {
            switch (event.type) {
              case 'page-start':
                s.currentPage = event.label;
                s.sections = [];
                s.builtSectionIds = [];
                s.pageStates[event.template] = 'running';
                break;
              case 'page-done':
                s.pageStates[event.template] = 'done';
                break;
              case 'page-absent':
                s.pageStates[event.template] = 'absent';
                break;
              case 'section-start':
                s.activity = event.activity;
                s.sections.push({
                  sectionId: event.sectionId,
                  state: 'running',
                  activity: event.activity,
                });
                break;
              case 'section-done': {
                const row = s.sections.find((r) => r.sectionId === event.sectionId);
                if (row) row.state = 'done';
                s.builtSectionIds.push(event.sectionId);
                break;
              }
              case 'section-failed': {
                const row = s.sections.find((r) => r.sectionId === event.sectionId);
                if (row) row.state = 'failed';
                // Failed = the theme copy stands — the section still joins the page (§5.3).
                s.builtSectionIds.push(event.sectionId);
                break;
              }
              case 'image-start':
                s.activity = bt('chrome.gen.act.mood_image', 'Generating the mood image…');
                break;
              case 'image-done':
                s.activity = null;
                break;
              case 'section-skipped':
                s.sections.push({
                  sectionId: event.sectionId,
                  state: event.reason === 'integrity' ? 'integrity' : 'keep',
                });
                // Kept sections assemble in order too — the page builds top to bottom, no gaps.
                s.builtSectionIds.push(event.sectionId);
                if (event.reason === 'integrity' && !s.integritySkips.includes(event.sectionType)) {
                  s.integritySkips.push(event.sectionType);
                }
                break;
              default:
                break;
            }
          });
        },
      );

      await productPhotosTask;

      if (summary.status === 'done') {
        sessionStorage.setItem(doneKey(get().runId, categoryId, storeDescription), '1');
      }
      set((s) => {
        s.status = summary.status === 'unavailable' ? 'unavailable' : 'done';
        s.summary = summary;
        s.activity = null;
        s.currentPage = null;
      });
    },

    async regenerateSection(sectionId, instruction, surfaces, options = {}) {
      if (get().regenSectionId) return; // one brick at a time — the UX stays legible
      const builder = () => useBuilderStore.getState();
      set((s) => {
        s.regenSectionId = sectionId;
        s.regenError = null;
        s.regenProgress = null;
        s.regenResult = null;
      });
      let textsWritten = 0;
      let imagesDone = 0;
      let failedBatches = 0;
      try {
        const doc = serializeComposed(builder());
        const section = doc.sections.find((sec) => sec.id === sectionId);
        if (!section) return;
        const narrowed = { ...doc, sections: [section] };
        const locale = builder().route.locale || builder().storeLanguage || 'fr';
        const consigne = instruction.trim() || 'Améliore cette section : plus clair, plus vendeur.';
        const scope = options.scope ?? { sectionId };

        // LE CONTEXTE, AVANT LA CONSIGNE (26/08). Le catalogue est chargé une fois pour les deux
        // surfaces : c'est lui qui dit « ce que vend cette boutique », et c'est ce qui manquait le
        // plus au modèle — un « rends ça plus vendeur » sans savoir ce qui est vendu.
        try {
          await builder().loadCatalog();
        } catch {
          // pas de catalogue — la section porte encore son sujet dans ses propres textes
        }
        const activeProducts = builder().catalog.filter(
          (p) => p.status?.toUpperCase() === 'ACTIVE',
        );
        const bound = builder().previewProduct;
        const product: RetouchProduct | null = bound
          ? {
              name: bound.name,
              description: bound.description.replace(/<[^>]+>/g, ' '),
              price: builder().renderContext?.formatMoney?.(bound.price),
            }
          : null;
        const catalogNames = activeProducts
          .filter((p) => p.id !== bound?.id)
          .slice(0, 6)
          .map((p) => p.name)
          .filter(Boolean);
        const scopeName = scopeTargetName(builder(), scope);
        const brief = buildRetouchBrief({
          instruction: consigne,
          storeName: builder().storeName || builder().storeSlug || 'La boutique',
          catalogNames: product ? [product.name, ...catalogNames] : catalogNames,
          product,
          pageTemplate: builder().route.template,
          pageHandle: builder().route.handle,
          pageOutline: builder()
            .order.map((id) => builder().sectionsById[id])
            .filter((sec): sec is NonNullable<typeof sec> => !!sec && !sec.disabled)
            .map((sec) => sectionDisplayName(sec)),
          sectionName: sectionDisplayName(section),
          scopeWording: scopeName.wording,
        });

        if (surfaces.texts) {
          // LA PORTÉE EST CELLE DE LA SÉLECTION (26/08) : un bloc désigné, un bloc retouché — les
          // six autres de la section ne bougent pas. Une sélection qui ne relève aucun champ n'appelle
          // rien (et ne dépense rien) plutôt que de retomber sur la section entière.
          const slots = collectTextSlots(narrowed, sectionRegistry).filter((slot) =>
            slotInScope(slot, scope),
          );
          const dto = slots.map(toSlotDto).filter((d): d is NonNullable<typeof d> => d !== null);
          // PAR PAQUETS, PARCE QU'UN SEUL APPEL NE TIENT PAS (27/08, lu dans le journal des appels
          // IA) : vingt-cinq champs = 109 s de rédaction, au-delà du délai d'un appel — donc une
          // reprise, puis un échec à 120 s, et rien à l'écran. Un paquet court revient en quelques
          // secondes, et un paquet qui rate n'emporte pas les autres.
          const batches: (typeof dto)[] = [];
          for (let i = 0; i < dto.length; i += TEXT_BATCH)
            batches.push(dto.slice(i, i + TEXT_BATCH));
          if (batches.length > 0) {
            set((s) => {
              s.regenProgress = { done: 0, total: batches.length };
            });
          }
          // On ACCUMULE et on applique UNE fois : « Ctrl-Z » doit défaire la retouche entière, pas
          // un paquet sur quatre — c'est le bouton « j'aime pas » du fondateur.
          const patches = [];
          for (const [index, batch] of batches.entries()) {
            try {
              const res = await aiApi.generateSection({
                capability: 'SECTION',
                locale,
                voice: '',
                role: brief.role,
                storeName: brief.storeName,
                storeCategory: '',
                storeDescription: brief.storeDescription,
                productContext: brief.productContext,
                // CE QUE LES CLIENTES DISENT, quand cette page est liée à un produit : le serveur va
                // chercher le résumé de son étude. `product` est déjà le produit de la page (ou celui
                // de l'aperçu) — la même source que le contexte juste au-dessus, jamais une seconde.
                productId: bound?.id,
                slots: batch,
                rewrite: true,
                provider: options.textProvider,
              });
              patches.push(...valuesToPatches(res.values));
            } catch (e) {
              // Une clé absente arrête TOUT (rien n'ira mieux au paquet suivant) ; un refus du
              // fournisseur ne coûte que son paquet.
              if (e instanceof AiUnavailableError) throw e;
              failedBatches += 1;
            }
            set((s) => {
              s.regenProgress = { done: index + 1, total: batches.length };
            });
          }
          if (patches.length > 0) {
            textsWritten = builder().applyGeneratedText(patches).applied;
          }
        }

        if (surfaces.image) {
          // THE SUBJECT TRAVELS OR THE MODEL INVENTS ONE (the 2026-08-21 handbag: a regen prompt
          // that carried only «améliore» + «photographie e-commerce» produced the archetypal
          // e-commerce product — a bag — in a headphones store). Ground every regen image in what
          // ACTUALLY stands: the section's own current copy + the store's real product names, and
          // the product cover as visual reference whenever it is unambiguous.
          const sectionText = collectTextSlots(narrowed, sectionRegistry)
            .filter((slot) => slotInScope(slot, scope))
            .map((sl) => sl.current)
            .filter(Boolean)
            .join(' ')
            .replace(/\s+/g, ' ')
            .slice(0, 400);
          const subject = buildImageSubject({
            sectionText,
            product,
            catalogNames: catalogNames.slice(0, 4),
          });
          const slots = collectImageSlots(narrowed, sectionRegistry).filter(
            (sl) => sl.generatable && slotInScope(sl, scope),
          );
          const targets = slots.slice(0, 4).flatMap((sl) => {
            const sid = sl.path[0];
            if (!sid) return [];
            return [
              {
                sectionId: sid,
                blockId: sl.path.length > 1 ? sl.path[sl.path.length - 1] : undefined,
                fieldId: sl.fieldId,
                sectionType: sl.sectionType,
                context: sl.context,
                hadImage: sl.hasCurrentImage,
                generatable: true,
              },
            ];
          });
          // Visual reference: the page's bound product, else the store's ONLY product — never a
          // guess between several (the wrong cover would be worse than none; the text subject holds).
          const previewId =
            builder().previewProduct?.id ??
            (activeProducts.length === 1 ? activeProducts[0]?.id : undefined);
          for (const target of targets) {
            // Remember what stands so a failed regeneration RESTORES it — «s'il n'aime pas» goes
            // through Ctrl-Z, never through a broken slot.
            const prev = readImageValue(builder(), target);
            const alt = target.context || consigne.slice(0, 120);
            try {
              const applyTo = (url: string) => {
                if (target.blockId) {
                  builder().updateBlockSettings(target.sectionId, target.blockId, {
                    [target.fieldId]: { url, alt },
                  });
                } else {
                  builder().updateSectionSettings(target.sectionId, {
                    [target.fieldId]: { url, alt },
                  });
                }
              };
              applyTo(GENERATING_MEDIA_URI);
              const prompt =
                `${consigne} Photographie e-commerce professionnelle. ${subject} ` +
                `${target.context ? `Emplacement précis : ${target.context.replace(/\s+/g, ' ')}. ` : ''}` +
                "L'image montre ce que vend CETTE boutique — jamais un autre produit. " +
                'Sans texte ajouté, sans lettrage.';
              const url = await aiApi
                .generateImageStream(
                  {
                    prompt,
                    aspectRatio: '16:9',
                    productId: previewId,
                    provider: options.imageProvider,
                  },
                  (dataUri) => applyTo(dataUri),
                )
                .catch(() =>
                  aiApi.generateImage({
                    prompt,
                    aspectRatio: '16:9',
                    provider: options.imageProvider,
                  }),
                );
              applyTo(url);
              imagesDone += 1;
            } catch {
              // restore what stood — never a broken slot, never a stranded animation
              if (target.blockId) {
                builder().updateBlockSettings(target.sectionId, target.blockId, {
                  [target.fieldId]: prev,
                });
              } else {
                builder().updateSectionSettings(target.sectionId, { [target.fieldId]: prev });
              }
              set((s) => {
                s.regenError = 'image';
              });
            }
          }
        }
      } catch (e) {
        set((s) => {
          s.regenError = e instanceof AiUnavailableError ? 'indisponible' : 'echec';
        });
      } finally {
        set((s) => {
          s.regenSectionId = null;
          s.regenProgress = null;
          // CE QUI A CHANGÉ SE DIT (27/08). La bulle se refermait sur l'envoi : un échec, ou une
          // consigne que le modèle a jugée sans effet, laissait la vendeuse devant une page
          // identique, sans un mot — impossible de distinguer « ça n'a pas marché » de « il a
          // trouvé que c'était déjà bien ».
          s.regenResult = { texts: textsWritten, images: imagesDone, failedBatches };
        });
      }
    },

    async publishStore() {
      if (get().publishState === 'publishing' || get().publishState === 'published') return;
      set((s) => {
        s.publishState = 'publishing';
      });
      try {
        // The existing theme-publish path IS the mechanism: copy every workspace draft over the live
        // handle and publish — the one moment the customer-facing store changes. Nothing bespoke.
        const ok = await useBuilderStore.getState().publishTheme();
        if (!ok) throw new Error('publish refused');
        let slug = useBuilderStore.getState().storeSlug;
        if (!slug) {
          await useBuilderStore.getState().loadStore();
          slug = useBuilderStore.getState().storeSlug;
        }
        set((s) => {
          s.publishState = 'published';
          s.storefrontUrl = slug
            ? deriveStorefrontUrl(slug, useBuilderStore.getState().storePrimaryDomain)
            : null;
        });
      } catch {
        set((s) => {
          s.publishState = 'failed';
        });
      }
    },

    explore() {
      // Walk the store as a customer would: the builder's existing preview mode IS the walkthrough.
      useBuilderStore.getState().setPreviewMode(true);
      get().dismiss();
      // AFTER dismiss (which resets the scene state): inside the dashboard modal, the walkthrough is
      // ALL the iframe may ever show — the editor lives outside (see the interface doc).
      if (window.self !== window.top) {
        set((s) => {
          s.embeddedPreview = true;
        });
      }
    },

    openEditor() {
      // EMBEDDED (the dashboard's creation modal runs the builder in an iframe): «Ouvrir le builder»
      // must break OUT of the modal into a real builder tab — editing inside a modal is a trap.
      // A cross-origin window only allows `location.href =` and `location.replace()` — calling
      // `.assign()` throws SecurityError, which is exactly how this button died on 2026-08-21.
      if (window.self !== window.top) {
        // ON THE GENERATED PAGES, not the builder's defaults: a bare URL boots template=index on the
        // `default` workspace — the seller landed on their OLD store and read the generation as lost
        // (founder 2026-08-21). The current route IS the generated workspace; carry it whole.
        const route = useBuilderStore.getState().route;
        const qs = new URLSearchParams();
        if (route.template) qs.set('template', route.template);
        if (route.handle) qs.set('handle', route.handle);
        if (route.locale) qs.set('locale', route.locale);
        const url = `${window.location.origin}${window.location.pathname}?${qs.toString()}`;
        try {
          window.top!.location.href = url;
        } catch {
          window.open(url, '_blank'); // sandboxed top? a new tab still gets the seller there
        }
        return;
      }
      useBuilderStore.getState().setPreviewMode(false);
      get().dismiss();
    },

    dismiss() {
      set((s) => {
        s.status = 'idle';
        s.mode = 'panel';
        s.categoryId = null;
        s.initialBrief = '';
        s.productId = null;
        s.sections = [];
        s.builtSectionIds = [];
        s.summary = null;
        s.integritySkips = [];
        s.productPhotos = 0;
        s.publishState = 'idle';
        s.storefrontUrl = null;
      });
    },
  })),
);
